export type FaceAnchor = { cx: number; cy: number };

export type FaceBounds = {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
  cx: number;
  cy: number;
  area: number;
};

type NormPoint = { x: number; y: number };

export function computeFaceBounds(landmarks: NormPoint[]): FaceBounds {
  let minX = Number.POSITIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;

  for (const p of landmarks) {
    const x = Number(p.x);
    const y = Number(p.y);
    if (!Number.isFinite(x) || !Number.isFinite(y)) continue;
    if (x < minX) minX = x;
    if (y < minY) minY = y;
    if (x > maxX) maxX = x;
    if (y > maxY) maxY = y;
  }

  if (
    !Number.isFinite(minX) ||
    !Number.isFinite(minY) ||
    !Number.isFinite(maxX) ||
    !Number.isFinite(maxY)
  ) {
    return { minX: 0, minY: 0, maxX: 0, maxY: 0, cx: 0, cy: 0, area: 0 };
  }

  const w = Math.max(0, maxX - minX);
  const h = Math.max(0, maxY - minY);
  return {
    minX,
    minY,
    maxX,
    maxY,
    cx: minX + w / 2,
    cy: minY + h / 2,
    area: w * h,
  };
}

export function computeFaceBoundsList(faces: NormPoint[][]): FaceBounds[] {
  return faces.map((lm) => computeFaceBounds(lm));
}

export function pickFaceIndexByAnchor(bounds: FaceBounds[], anchor: FaceAnchor): number {
  let bestIdx = 0;
  let bestD2 = Number.POSITIVE_INFINITY;
  for (let i = 0; i < bounds.length; i++) {
    const b = bounds[i];
    const dx = b.cx - anchor.cx;
    const dy = b.cy - anchor.cy;
    const d2 = dx * dx + dy * dy;
    if (d2 < bestD2) {
      bestD2 = d2;
      bestIdx = i;
    }
  }
  return bestIdx;
}

export function pickLargestFaceIndex(bounds: FaceBounds[]): number {
  let bestIdx = 0;
  let bestArea = -1;
  for (let i = 0; i < bounds.length; i++) {
    const area = bounds[i].area;
    if (area > bestArea) {
      bestArea = area;
      bestIdx = i;
    }
  }
  return bestIdx;
}

export type CoverTransform = { scaledW: number; scaledH: number; offsetX: number; offsetY: number };

export function getObjectFitCoverTransform(
  elementW: number,
  elementH: number,
  videoW: number,
  videoH: number,
): CoverTransform {
  if (!(elementW > 0) || !(elementH > 0) || !(videoW > 0) || !(videoH > 0)) {
    return { scaledW: elementW, scaledH: elementH, offsetX: 0, offsetY: 0 };
  }
  const scale = Math.max(elementW / videoW, elementH / videoH);
  const scaledW = videoW * scale;
  const scaledH = videoH * scale;
  const offsetX = (elementW - scaledW) / 2;
  const offsetY = (elementH - scaledH) / 2;
  return { scaledW, scaledH, offsetX, offsetY };
}

export type ElementRect = { x: number; y: number; w: number; h: number; cx: number; cy: number };

export function mapBoundsToElementRect(bounds: FaceBounds, t: CoverTransform): ElementRect {
  const x = bounds.minX * t.scaledW + t.offsetX;
  const y = bounds.minY * t.scaledH + t.offsetY;
  const w = (bounds.maxX - bounds.minX) * t.scaledW;
  const h = (bounds.maxY - bounds.minY) * t.scaledH;
  return {
    x,
    y,
    w,
    h,
    cx: bounds.cx * t.scaledW + t.offsetX,
    cy: bounds.cy * t.scaledH + t.offsetY,
  };
}

export function pickFaceIndexByClick(
  bounds: FaceBounds[],
  clickX: number,
  clickY: number,
  transform: CoverTransform,
): number | null {
  if (!bounds.length) return null;

  let bestIdx: number | null = null;
  let bestScore = Number.POSITIVE_INFINITY;

  for (let i = 0; i < bounds.length; i++) {
    const rect = mapBoundsToElementRect(bounds[i], transform);
    const inside =
      clickX >= rect.x &&
      clickX <= rect.x + rect.w &&
      clickY >= rect.y &&
      clickY <= rect.y + rect.h;
    if (!inside) continue;
    const dx = clickX - rect.cx;
    const dy = clickY - rect.cy;
    const score = dx * dx + dy * dy;
    if (score < bestScore) {
      bestScore = score;
      bestIdx = i;
    }
  }

  if (bestIdx !== null) return bestIdx;

  // Fallback: nearest face center even if the click was outside any box.
  for (let i = 0; i < bounds.length; i++) {
    const rect = mapBoundsToElementRect(bounds[i], transform);
    const dx = clickX - rect.cx;
    const dy = clickY - rect.cy;
    const score = dx * dx + dy * dy;
    if (score < bestScore) {
      bestScore = score;
      bestIdx = i;
    }
  }

  return bestIdx;
}

